import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ClipboardList, Plus, Trash2, ArrowLeft, AlertTriangle, CheckCircle, Loader2, Printer, Mail, Download, Search, Barcode as BarcodeIcon } from 'lucide-react';
import api from '../api';

const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function InventoryReports() {
  const [view, setView] = useState('list'); // list | detail
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // active count detail
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const scanRef = useRef(null);
  const scanTimerRef = useRef(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.request('/api/inventory-counts');
      setReports(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const openReport = async (id) => {
    setBusy(true);
    try {
      const data = await api.request(`/api/inventory-counts/${id}`);
      setActive(data);
      setView('detail');
      setSearch('');
    } catch (e) { alert('Failed to open: ' + e.message); }
    finally { setBusy(false); }
  };

  const createReport = async () => {
    setBusy(true);
    try {
      const data = await api.request('/api/inventory-counts', {
        method: 'POST', body: JSON.stringify({}),
      });
      await loadReports();
      await openReport(data.id);
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const deleteReport = async (r) => {
    const msg = r.status === 'applied'
      ? `WARNING: This will REVERT inventory counts for "${r.name}".\n\nAll sales orders, returns, and purchases that occurred AFTER this count was applied will be preserved.\n\nDelete and revert?`
      : `Delete inventory count "${r.name}"?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.request(`/api/inventory-counts/${r.id}`, { method: 'DELETE' });
      await loadReports();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  // Update an item's new_qty (manual entry)
  const setItemQty = (productId, val) => {
    const numVal = val === '' ? null : parseInt(val, 10);
    if (val !== '' && (isNaN(numVal) || numVal < 0)) return;
    setActive(prev => ({
      ...prev,
      items: prev.items.map(it =>
        it.product_id === productId
          ? { ...it, new_qty: numVal, counted: numVal !== null }
          : it
      ),
    }));
  };

  // Save edited counts to backend (debounced via an explicit Save button to keep network low)
  const saveCounts = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const updates = (active.items || [])
        .filter(it => it.counted)
        .map(it => ({ product_id: it.product_id, new_qty: it.new_qty }));
      await api.request(`/api/inventory-counts/${active.id}/items`, {
        method: 'PUT', body: JSON.stringify({ updates }),
      });
      await openReport(active.id);
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  // Barcode scan: increments by 1, then user can edit qty
  const handleScan = async (sku) => {
    const cleanSku = (sku || '').trim();
    if (!cleanSku || !active) return;
    try {
      const res = await api.request(`/api/inventory-counts/${active.id}/scan`, {
        method: 'POST', body: JSON.stringify({ sku: cleanSku }),
      });
      // update the item locally
      const scanned = res.item;
      if (scanned) {
        setActive(prev => ({
          ...prev,
          counted_items: res.counted_items,
          discrepancies: res.discrepancies,
          items: prev.items.map(it => it.product_id === scanned.product_id ? { ...it, ...scanned } : it),
        }));
      }
      setScanInput('');
      // refocus
      if (scanRef.current) scanRef.current.focus();
    } catch (e) {
      alert('Scan failed: ' + e.message);
      setScanInput('');
    }
  };

  // Auto-scan when input is followed by "enter" or after small idle (barcode guns)
  const onScanInputChange = (val) => {
    setScanInput(val);
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    // If looks like a complete barcode, auto-trigger after 250ms idle
    if (val.length >= 4) {
      scanTimerRef.current = setTimeout(() => handleScan(val), 250);
    }
  };

  const onScanKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
      handleScan(scanInput);
    }
  };

  const applyCounts = async () => {
    setShowApplyConfirm(false);
    setBusy(true);
    try {
      const res = await api.request(`/api/inventory-counts/${active.id}/apply`, { method: 'POST' });
      alert(res.message || 'Counts applied.');
      await openReport(active.id);
      await loadReports();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    if (!active) return;
    try {
      const token = localStorage.getItem('access_token');
      const resp = await fetch(`${API_URL}/api/inventory-counts/${active.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(active.name || 'inventory-count').replace(/\s+/g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed: ' + e.message); }
  };

  const sendEmail = async () => {
    if (!emailAddr.trim()) { alert('Enter an email address'); return; }
    setEmailSending(true);
    try {
      await api.request(`/api/inventory-counts/${active.id}/email`, {
        method: 'POST', body: JSON.stringify({ to_email: emailAddr.trim() }),
      });
      alert(`Report emailed to ${emailAddr}`);
      setShowEmail(false); setEmailAddr('');
    } catch (e) { alert('Email failed: ' + e.message); }
    finally { setEmailSending(false); }
  };

  // ---- LIST VIEW ----
  if (view === 'list') {
    return (
      <div className="p-6 space-y-6" data-testid="inventory-reports-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-blue-600" /> Inventory Reports
            </h1>
            <p className="text-sm text-gray-500 mt-1">Run physical counts, see discrepancies, and apply or revert them.</p>
          </div>
          <button
            onClick={createReport}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            data-testid="new-inventory-report-btn"
          >
            <Plus className="h-4 w-4" /> New Inventory Report
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date Run</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Applied</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Items</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Counted</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Discrepancies</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-8 text-gray-400">Loading…</td></tr>
              ) : reports.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-gray-400">No inventory reports yet. Click "New Inventory Report" to start one.</td></tr>
              ) : reports.map(r => (
                <tr key={r.id} className="border-t hover:bg-gray-50" data-testid={`inv-report-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => openReport(r.id)} className="text-blue-600 hover:underline font-medium" data-testid={`open-report-${r.id}`}>
                      {r.name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-gray-700">{r.applied_at ? fmtDate(r.applied_at) : '—'}</td>
                  <td className="px-4 py-3 text-right">{r.total_items}</td>
                  <td className="px-4 py-3 text-right">{r.counted_items}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${r.discrepancies > 0 ? 'text-red-600' : 'text-gray-500'}`}>{r.discrepancies}</td>
                  <td className="px-4 py-3">
                    {r.status === 'applied'
                      ? <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Applied</span>
                      : <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">In Progress</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => deleteReport(r)}
                      className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                      title={r.status === 'applied' ? 'Delete + revert quantities' : 'Delete'}
                      data-testid={`delete-report-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ---- DETAIL VIEW ----
  const items = active?.items || [];
  const filtered = items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.name || '').toLowerCase().includes(q) || (it.sku || '').toLowerCase().includes(q);
  });
  const counted = items.filter(it => it.counted).length;
  const discrepancies = items.filter(it => it.counted && it.new_qty !== null && it.new_qty !== it.system_qty);
  const isApplied = active?.status === 'applied';

  return (
    <div className="p-6 space-y-4" data-testid="inventory-report-detail">
      <button onClick={() => { setView('list'); setActive(null); loadReports(); }} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900" data-testid="back-to-list-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Reports
      </button>

      {/* Header card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{active?.name}</h1>
            <p className="text-xs text-gray-500 mt-1">
              Date Run: <span className="font-medium text-gray-700">{fmtDate(active?.created_at)}</span>
              {active?.applied_at && <> &nbsp;•&nbsp; Applied: <span className="font-medium text-gray-700">{fmtDate(active?.applied_at)}</span></>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="print-report-btn">
              <Printer className="h-4 w-4" /> Print
            </button>
            <button onClick={downloadPDF} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="download-pdf-btn">
              <Download className="h-4 w-4" /> Download PDF
            </button>
            <button onClick={() => setShowEmail(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="email-report-btn">
              <Mail className="h-4 w-4" /> Email
            </button>
            {!isApplied && (
              <>
                <button onClick={saveCounts} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-white rounded-md text-sm hover:bg-gray-800 disabled:opacity-50" data-testid="save-counts-btn">
                  Save Counts
                </button>
                <button
                  onClick={() => setShowApplyConfirm(true)}
                  disabled={busy || counted === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50"
                  data-testid="apply-counts-btn"
                >
                  <CheckCircle className="h-4 w-4" /> Save New Counts to App
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-gray-50 rounded p-3">
            <p className="text-[11px] uppercase tracking-wider text-gray-500">Total Items</p>
            <p className="text-lg font-bold text-gray-900">{items.length}</p>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <p className="text-[11px] uppercase tracking-wider text-gray-500">Counted</p>
            <p className="text-lg font-bold text-blue-700">{counted}</p>
          </div>
          <div className="bg-gray-50 rounded p-3">
            <p className="text-[11px] uppercase tracking-wider text-gray-500">Not Counted</p>
            <p className="text-lg font-bold text-amber-600">{items.length - counted}</p>
          </div>
          <div className="bg-red-50 rounded p-3">
            <p className="text-[11px] uppercase tracking-wider text-red-600">Discrepancies</p>
            <p className="text-lg font-bold text-red-700">{discrepancies.length}</p>
          </div>
        </div>
      </div>

      {/* Barcode + Search bar */}
      {!isApplied && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-col md:flex-row gap-3 items-stretch print:hidden">
          <div className="flex-1 flex items-center gap-2">
            <BarcodeIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <input
              ref={scanRef}
              type="text"
              value={scanInput}
              onChange={(e) => onScanInputChange(e.target.value)}
              onKeyDown={onScanKey}
              placeholder="Scan barcode or type SKU + Enter (auto-increments quantity by 1)"
              className="flex-1 px-3 py-2 border border-blue-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="barcode-scan-input"
              autoFocus
            />
            <button onClick={() => handleScan(scanInput)} className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Scan</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-3 print:hidden">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products by name or SKU…"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="search-products-input"
          />
        </div>
      </div>

      {/* Items table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600">SKU</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Product</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">System Qty</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">New Count</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">Discrepancy</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 1000).map(it => {
              const hasDiscrepancy = it.counted && it.new_qty !== null && it.new_qty !== it.system_qty;
              const diff = it.counted && it.new_qty !== null ? it.new_qty - it.system_qty : null;
              return (
                <tr key={it.product_id} className={`border-t ${hasDiscrepancy ? 'bg-red-50' : ''}`} data-testid={`item-row-${it.product_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{it.sku}</td>
                  <td className="px-3 py-2">{it.name}</td>
                  <td className="px-3 py-2 text-right">{it.system_qty}</td>
                  <td className="px-3 py-2 text-right">
                    {isApplied ? (
                      <span className={hasDiscrepancy ? 'font-bold text-red-600' : ''}>{it.new_qty ?? '—'}</span>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        value={it.new_qty ?? ''}
                        onChange={(e) => setItemQty(it.product_id, e.target.value)}
                        className={`w-24 px-2 py-1 border rounded text-right text-sm ${hasDiscrepancy ? 'border-red-400 bg-red-50 font-bold text-red-700' : 'border-gray-300'}`}
                        data-testid={`qty-input-${it.product_id}`}
                      />
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${hasDiscrepancy ? 'text-red-600' : 'text-gray-400'}`}>
                    {diff === null ? '' : (diff > 0 ? `+${diff}` : diff)}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-gray-400">No products found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Discrepancies summary at bottom (red) */}
      {discrepancies.length > 0 && (
        <div className="bg-white border-2 border-red-300 rounded-lg p-4 mt-4" data-testid="discrepancies-summary">
          <h3 className="text-base font-bold text-red-700 flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5" /> Discrepancies ({discrepancies.length})
          </h3>
          <table className="w-full text-sm">
            <thead className="bg-red-50">
              <tr>
                <th className="text-left px-3 py-2 text-red-700">SKU</th>
                <th className="text-left px-3 py-2 text-red-700">Product</th>
                <th className="text-right px-3 py-2 text-red-700">System</th>
                <th className="text-right px-3 py-2 text-red-700">Counted</th>
                <th className="text-right px-3 py-2 text-red-700">Diff</th>
              </tr>
            </thead>
            <tbody>
              {discrepancies.map(it => {
                const diff = it.new_qty - it.system_qty;
                return (
                  <tr key={it.product_id} className="border-t border-red-100 text-red-700">
                    <td className="px-3 py-2 font-mono text-xs">{it.sku}</td>
                    <td className="px-3 py-2 font-medium">{it.name}</td>
                    <td className="px-3 py-2 text-right">{it.system_qty}</td>
                    <td className="px-3 py-2 text-right font-bold">{it.new_qty}</td>
                    <td className="px-3 py-2 text-right font-bold">{diff > 0 ? `+${diff}` : diff}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Apply Confirm Modal */}
      {showApplyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="apply-confirm-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-2 text-amber-600 mb-3">
              <AlertTriangle className="h-6 w-6" />
              <h3 className="text-lg font-bold">Warning</h3>
            </div>
            <p className="text-gray-700 mb-4">
              <strong>This will change the product counts for all {counted} items entered to the new amounts listed!</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {discrepancies.length > 0 && <>{discrepancies.length} item(s) will be adjusted. </>}
              You can revert this later by deleting the report — sales orders / returns made in between will be preserved.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowApplyConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="cancel-apply-btn">Cancel</button>
              <button onClick={applyCounts} className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700" data-testid="confirm-apply-btn">
                Yes, Apply Counts
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email modal */}
      {showEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><Mail className="h-5 w-5" /> Email Inventory Report</h3>
            <input
              type="email"
              value={emailAddr}
              onChange={(e) => setEmailAddr(e.target.value)}
              placeholder="recipient@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              data-testid="email-recipient-input"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowEmail(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={sendEmail} disabled={emailSending} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2" data-testid="send-email-btn">
                {emailSending && <Loader2 className="h-4 w-4 animate-spin" />} Send
              </button>
            </div>
          </div>
        </div>
      )}

      {busy && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <span className="text-sm">Working…</span>
        </div>
      )}
    </div>
  );
}
